import Compressor from "compressorjs";
import { Accept, DropEvent, FileRejection, FileWithPath } from "react-dropzone";

import { FileProps, UploadErrors } from "./type";

export const sizeLimitCalc = 1.048576;

export const defaultAllowedFileTypes: Accept = {
    "application/pdf": [],
    "image/jpg": [],
    "image/jpeg": [],
    "image/png": [],
};
export const defaultMaxNumberOfFiles = 5;
export const defaultMaxFileSize = 2 * 1000000; // Bytes
export const defaultMaxTotalSize = (maxFileSize: number, maxNumberOfFiles: number): number =>
    maxFileSize * maxNumberOfFiles; // Bytes

const maxCompressedFileSize = 20 * 1000000; // 20 MB
export const compressFiles = async (filesEvent: FileSystemFileHandle[] | DropEvent): Promise<Array<File>> => {
    let files;

    if (Array.isArray(filesEvent)) {
        files = await Promise.all(filesEvent.map(async (f) => await f.getFile()));
    } else if ("target" in filesEvent && "files" in filesEvent.target) {
        files = Array.from(filesEvent.target.files);
    } else if ("dataTransfer" in filesEvent) {
        files = Array.from(filesEvent.dataTransfer.files);
    }

    return await Promise.all(
        files.map(async (file) => {
            // No compression for non-image files or files larger than 20 MB or less than 2 MB
            if (!file.type.startsWith("image") || file.size > maxCompressedFileSize || file.size < defaultMaxFileSize)
                return file;

            let quality = 0.8; // default lossy compression quality

            let newFile = file;

            while (quality > 0.1 && newFile.size > defaultMaxFileSize) {
                // compress until file size is less than 2 MB

                newFile = await new Promise(
                    (resolve, reject) =>
                        new Compressor(file, {
                            quality,
                            success: (compressedFile: File) => resolve(compressedFile),
                            error: reject,
                        }),
                );

                quality -= 0.2;
            }
            return new File([newFile], file.name, { type: file.type });
        }),
    );
};

export const getAcceptedFilesTotalSize = (files: FileProps[] | FileWithPath[]): number => {
    let count = 0;
    files.forEach((file: FileProps | FileWithPath) => {
        count += file.size;
    });
    return count;
};

export const getReceivedErrors = (files: FileRejection[]): UploadErrors => {
    const filesInvalidType = files
        .filter((f) => f.errors.find((e) => e.code === "file-invalid-type"))
        .map((f) => f.file.name);

    const filesInvalidSize = files
        .filter((f) => f.errors.find((e) => e.code === "file-too-large"))
        .map((f) => f.file.name);

    return { filesInvalidType, filesInvalidSize };
};

export const getHasFileErrors = (errors: UploadErrors): boolean =>
    errors.filesDuplicated.length > 0 ||
    errors.filesInvalidSize.length > 0 ||
    errors.filesInvalidType.length > 0 ||
    errors.filesTotalSizeExceeded.length > 0 ||
    errors.filesUploadFailed.length > 0;
